import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import React, { ChangeEventHandler, FC, ReactNode, useRef } from "react";

const DEFAULT_LABEL_COLOR = "#343c4399";
const ERROR_COLOR = "#d32f2f";
const WHITE_COLOR = "#ffffff";

interface UserFormTextFieldProps {
  label: ReactNode;
  value: unknown;
  id?: string;
  name?: string;
  disabled?: boolean;
  onBlur?: () => void;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  isAlternateColor?: boolean;
  error?: boolean;
  errorMessage?: string;
  maxLength?: number;
  autoFocus?: boolean;
  selectAllOnInitialFocus?: boolean;
  /** data-testid placed on the underlying <input> */
  inputTestId?: string;
}

export const UserFormTextField: FC<UserFormTextFieldProps> = (props) => {
  const performedSelectRef = useRef(false);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (props.selectAllOnInitialFocus && !performedSelectRef.current) {
      event.target.select();
      performedSelectRef.current = true;
    }
  };

  // Dynamically generate styles based on component state
  const colors = {
    label: props.error
      ? ERROR_COLOR
      : props.isAlternateColor
        ? WHITE_COLOR
        : DEFAULT_LABEL_COLOR,
    input: props.isAlternateColor ? WHITE_COLOR : undefined,
    underline: props.error
      ? ERROR_COLOR
      : props.isAlternateColor
        ? WHITE_COLOR
        : undefined,
    focusUnderline: props.error ? ERROR_COLOR : "transparent",
  };

  const styles = {
    textField: {
      "& .MuiInput-underline:before": {
        borderBottomColor: colors.underline,
      },
      "& .MuiInput-underline:hover:before": {
        borderBottomColor: colors.underline,
        borderBottomWidth: "1px !important",
      },
      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottomColor: colors.underline,
      },
      "& .MuiInput-underline:after": {
        borderBottomColor: colors.focusUnderline,
        borderBottomWidth: "1px !important",
        transition: "none",
      },
    },
    slotProps: {
      inputLabel: {
        style: { color: colors.label },
      },
      input: {
        style: { color: colors.input },
      },
      htmlInput: {
        maxLength: props.maxLength,
        "data-testid": props.inputTestId,
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        pointerEvents: props.disabled ? "none" : "auto",
        opacity: props.disabled ? 0.5 : 1,
      }}
    >
      <TextField
        id={props.id}
        label={props.label}
        name={props.name}
        fullWidth
        variant="standard"
        autoFocus={props.autoFocus}
        onFocus={handleFocus}
        onBlur={props.onBlur}
        slotProps={styles.slotProps}
        sx={styles.textField}
        value={props.value ?? ""}
        onChange={props.onChange}
        error={props.error}
        helperText={props.error ? props.errorMessage : undefined}
      />
    </Box>
  );
};
